import { ref, uploadBytes, getDownloadURL, getBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions, getFirebaseStorage } from './firebase';

const STORAGE_PREFIX = 'coupon-campaigns';
const MAX_DIMENSION = 2400;
const QUALITY = 0.92;
/** Smaller embedded copy for PDF export (Firestore doc size limit). */
const PDF_CACHE_MAX_DIMENSION = 1400;
const PDF_CACHE_QUALITY = 0.82;
const PDF_CACHE_MAX_DATA_URL_CHARS = 900_000;

export interface CouponTemplateUploadResult {
  url: string;
  /** JPEG data URL for PDF export; null if too large to store in Firestore. */
  pdfDataUrl: string | null;
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9.-]/g, '_').slice(0, 80);
}

function resizeBlobToCanvas(blob: Blob, maxDimension: number): Promise<HTMLCanvasElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width: w, height: h } = img;
      if (w > maxDimension || h > maxDimension) {
        if (w > h) {
          h = Math.round((h * maxDimension) / w);
          w = maxDimension;
        } else {
          w = Math.round((w * maxDimension) / h);
          h = maxDimension;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

function resizeToBlob(file: File, maxDimension: number, quality: number): Promise<Blob> {
  if (!file.type.startsWith('image/')) return Promise.resolve(file as unknown as Blob);

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width: w, height: h } = img;
      if (w > maxDimension || h > maxDimension) {
        if (w > h) {
          h = Math.round((h * maxDimension) / w);
          w = maxDimension;
        } else {
          w = Math.round((w * maxDimension) / h);
          h = maxDimension;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file as unknown as Blob);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      const mime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      canvas.toBlob((blob) => (blob ? resolve(blob) : resolve(file as unknown as Blob)), mime, quality);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file as unknown as Blob);
    };
    img.src = url;
  });
}

async function createPdfCacheDataUrl(blob: Blob): Promise<string | null> {
  const canvas = await resizeBlobToCanvas(blob, PDF_CACHE_MAX_DIMENSION);
  if (!canvas) return null;
  const dataUrl = canvas.toDataURL('image/jpeg', PDF_CACHE_QUALITY);
  if (dataUrl.length > PDF_CACHE_MAX_DATA_URL_CHARS) return null;
  return dataUrl;
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',');
  if (!base64) throw new Error('Invalid template image data URL.');
  const mime = header.match(/data:([^;]+)/)?.[1] ?? 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/** Upload a printable card background for a coupon / building-unit campaign. */
export async function uploadCouponCampaignTemplateImage(
  file: File,
  campaignId: string
): Promise<CouponTemplateUploadResult> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please choose a PNG or JPEG image.');
  }
  if (file.size > 8 * 1024 * 1024) {
    throw new Error('Image must be 8 MB or smaller.');
  }

  const storage = getFirebaseStorage();
  const baseName = sanitizeFilename(file.name.replace(/\.[^.]+$/, ''));
  const ext = file.type === 'image/png' ? 'png' : 'jpg';
  const blob = await resizeToBlob(file, MAX_DIMENSION, QUALITY);
  const pdfDataUrl = await createPdfCacheDataUrl(blob);
  const contentType = ext === 'png' ? 'image/png' : 'image/jpeg';
  const path = `${STORAGE_PREFIX}/${campaignId}/${Date.now()}-${baseName}.${ext}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, {
    contentType,
    cacheControl: 'public, max-age=31536000',
  });
  const url = await getDownloadURL(storageRef);
  return { url, pdfDataUrl };
}

/** Parse `coupon-campaigns/...` path from a Firebase Storage download URL. */
export function firebaseStoragePathFromDownloadUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes('firebasestorage.googleapis.com')) return null;
    const pathMatch = parsed.pathname.match(/\/o\/(.+)$/);
    if (!pathMatch?.[1]) return null;
    return decodeURIComponent(pathMatch[1]);
  } catch {
    return null;
  }
}

function mimeFromStoragePath(path: string): string {
  const lower = path.toLowerCase();
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.webp')) return 'image/webp';
  return 'image/png';
}

async function fetchCouponTemplateImageViaCallable(
  downloadUrl: string,
  storagePath: string
): Promise<Blob> {
  const fn = httpsCallable<
    { downloadUrl?: string; storagePath?: string },
    { dataUrl: string }
  >(getFirebaseFunctions(), 'fetchCouponTemplateImage');
  try {
    const result = await fn({ downloadUrl, storagePath });
    const dataUrl = result.data?.dataUrl;
    if (!dataUrl?.startsWith('data:')) {
      throw new Error('Template proxy returned an invalid image.');
    }
    return dataUrlToBlob(dataUrl);
  } catch (err: unknown) {
    const code =
      err && typeof err === 'object' && 'code' in err ? String((err as { code?: string }).code) : '';
    if (code === 'functions/not-found') {
      throw new Error(
        'PDF export needs the fetchCouponTemplateImage cloud function. Deploy with: firebase deploy --only functions:fetchCouponTemplateImage'
      );
    }
    throw err;
  }
}

/**
 * Load a coupon template image for PDF export.
 * Order: embedded data URL → cloud function → Storage SDK (requires bucket CORS).
 */
export async function fetchCouponTemplateImageBlob(
  url: string,
  cachedDataUrl?: string | null
): Promise<Blob> {
  if (cachedDataUrl?.startsWith('data:')) {
    return dataUrlToBlob(cachedDataUrl);
  }

  const storagePath = firebaseStoragePathFromDownloadUrl(url);
  if (storagePath) {
    try {
      return await fetchCouponTemplateImageViaCallable(url, storagePath);
    } catch (callableErr) {
      try {
        const storage = getFirebaseStorage();
        const bytes = await getBytes(ref(storage, storagePath));
        return new Blob([bytes], { type: mimeFromStoragePath(storagePath) });
      } catch {
        const detail =
          callableErr instanceof Error ? callableErr.message : 'Could not load template image.';
        throw new Error(
          `${detail} You can also re-upload the card design (stores an embedded PDF copy) or run .\\configure-storage-cors.ps1 to allow direct Storage reads.`
        );
      }
    }
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Could not load template image (${response.status}). Check the URL and try again.`);
  }
  return response.blob();
}

/** After a successful PDF export, persist a smaller embedded copy for faster future exports. */
export async function tryCreatePdfCacheDataUrl(templateUrl: string): Promise<string | null> {
  try {
    const blob = await fetchCouponTemplateImageBlob(templateUrl);
    return createPdfCacheDataUrl(blob);
  } catch {
    return null;
  }
}
